/* Open cards with native horizontal touch scrolling. */
if (!customElements.get('mecasy-product-information')) {
  customElements.define('mecasy-product-information', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      if (this.hasAttribute('data-structure-features')) this.querySelectorAll('.mcy-info__body').forEach(body => this.structureContent(body));
      const content = this.querySelector('.mcy-info__content');
      const cards = [...this.querySelectorAll('.mcy-info__card')];
      const links = [...this.querySelectorAll('.mcy-info__link')];
      // Mobile navigation is a single-panel switcher. No desktop markup or style changes.
      const mobile = window.matchMedia('(max-width: 749px)');
      const nav = this.querySelector('.mcy-info__nav');
      if (nav) nav.dataset.count = String(links.length);
      // Short names only on small screens: retain the configured text on desktop.
      const shortNames = {
        features: 'Caract.', description: 'Description', shipping: 'Livraison',
        compatibility: 'Compat.', safety: 'Sécurité', highlights: 'Atouts', advice: 'Conseils'
      };
      const titles = links.map(link => link.querySelector('span:not(.mcy-info__nav-arrow)'));
      const fullTitles = titles.map(title => title?.textContent ?? '');
      const syncTitles = () => titles.forEach((title, i) => {
        if (title) title.textContent = mobile.matches
          ? (shortNames[links[i].dataset.infoKind] || fullTitles[i]) : fullTitles[i];
      });
      // Reserve only the document height needed to keep the current scroll
      // position valid. Do this BEFORE hiding a tall panel (no scroll correction).
      const preserveScrollRange = () => {
        if (!mobile.matches) {
          content.style.removeProperty('min-height');
          return;
        }
        const rect = content.getBoundingClientRect();
        const trailingHeight = document.documentElement.scrollHeight - (window.scrollY + rect.bottom);
        const minimum = Math.max(0, window.innerHeight - rect.top - trailingHeight);
        content.style.minHeight = minimum > 0 ? Math.ceil(minimum) + 'px' : '';
      };
      // Release the reserve as the visitor scrolls up; never keep the tallest tab.
      window.addEventListener('scroll', () => {
        if (content.style.minHeight) preserveScrollRange();
      }, {passive: true, signal: this.controller.signal});
      window.addEventListener('resize', () => {
        if (content.style.minHeight) preserveScrollRange();
      }, {signal: this.controller.signal});
      const selectMobileCard = (index) => {
        if (!mobile.matches) return;
        cards.forEach((card, i) => { card.hidden = i !== index; });
        links.forEach((link, i) => {
          link.classList.toggle('is-active', i === index);
          link.setAttribute('aria-selected', String(i === index));
          if (i === index) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
      };
      const syncViewport = () => {
        if (!mobile.matches) content.style.removeProperty('min-height');
        syncTitles();
        if (mobile.matches) selectMobileCard(Math.max(0, links.findIndex(link => link.classList.contains('is-active'))));
        else { cards.forEach(card => { card.hidden = false; }); links.forEach(link => link.removeAttribute('aria-selected')); }
      };
      mobile.addEventListener('change', syncViewport, {signal: this.controller.signal});
      syncViewport();

      const controls = this.querySelector('.mcy-info__controls');
      const previous = this.querySelector('[data-info-prev]');
      const next = this.querySelector('[data-info-next]');
      const behavior = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      controls.hidden = cards.length < 2;
      [previous, next].forEach((button, index) => button.addEventListener('click', () => {
        const step = (cards[1]?.offsetLeft - cards[0]?.offsetLeft) || content.clientWidth;
        content.scrollBy({left: index === 0 ? -step : step, behavior: behavior()});
      }, {signal: this.controller.signal}));
      this.addEventListener('click', event => {
        const link = event.target.closest('.mcy-info__link');
        if (!link || !this.contains(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        const card = cards.find(card => '#' + card.id === link.getAttribute('href'));
        if (!card) return;
        event.preventDefault();
        if (mobile.matches) {
          // global.js delegates hash links on document and scrolls even when
          // defaultPrevented. Mobile tabs are local switches, not page anchors.
          event.stopPropagation();
          preserveScrollRange();
          selectMobileCard(cards.indexOf(card));
          return;
        }
        content.scrollBy({left: card.getBoundingClientRect().left - content.getBoundingClientRect().left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      }, {signal: this.controller.signal});
      const update = () => {
        if (mobile.matches) return;
        const left = content.getBoundingClientRect().left;
        const nearest = cards.reduce((best, card) => Math.abs(card.getBoundingClientRect().left-left) < Math.abs(best.getBoundingClientRect().left-left) ? card : best, cards[0]);
        links.forEach(link => {
          const active = link.getAttribute('href') === '#' + nearest?.id;
          link.classList.toggle('is-active', active);
          if (active) link.setAttribute('aria-current', 'location');
          else link.removeAttribute('aria-current');
        });
        previous.disabled = content.scrollLeft <= 2;
        next.disabled = content.scrollLeft >= content.scrollWidth - content.clientWidth - 2;
        const activeLink = links.find(link => link.getAttribute('href') === '#' + nearest?.id);
        const nav = activeLink?.parentElement;
        if (activeLink && nav) {
          const delta = activeLink.getBoundingClientRect().left - nav.getBoundingClientRect().left;
          if (delta < 0 || delta + activeLink.offsetWidth > nav.clientWidth) {
            nav.scrollTo({left: nav.scrollLeft + delta, behavior: behavior()});
          }
        }
      };
      content.addEventListener('scroll', update, {passive: true, signal: this.controller.signal});
      this.resizeObserver = new ResizeObserver(update);
      this.resizeObserver.observe(content);
      update();
    }
    structureContent(body) {
      if (!body || body.dataset.structured || body.querySelector('table,dl,img,video,iframe')) return;
      const rows = [];
      let row = [], valid = true;
      const flush = () => {
        if (row.some(node => node.textContent.trim())) rows.push(row);
        row = [];
      };
      const visit = parent => {
        for (const node of parent.childNodes) {
          if (node.nodeType === 3) { row.push(node.cloneNode(true)); continue; }
          if (node.nodeType !== 1) { valid = false; continue; }
          if (node.tagName === 'BR') { flush(); continue; }
          if (['P', 'DIV', 'UL', 'OL', 'LI'].includes(node.tagName)) {
            flush(); visit(node); flush();
          } else if (['STRONG', 'B', 'A', 'EM', 'I', 'SPAN', 'SUP', 'SUB'].includes(node.tagName)) row.push(node.cloneNode(true));
          else valid = false;
        }
      };
      visit(body); flush();
      if (!valid || rows.length < 2) return;
      const list = document.createElement('dl');
      list.className = 'mcy-info__specs';
      for (const nodes of rows) {
        while (nodes.length && !nodes[0].textContent.trim()) nodes.shift();
        const label = nodes.shift();
        if (!label || !['STRONG', 'B'].includes(label.tagName)) return;
        let name = label.textContent.trim();
        if (!name.endsWith(':')) {
          if (nodes[0]?.nodeType !== 3 || !/^\s*:/.test(nodes[0].textContent)) return;
          nodes[0].textContent = nodes[0].textContent.replace(/^\s*:\s*/, '');
        } else name = name.slice(0, -1).trim();
        if (!name || name.length > 90 || !nodes.some(node => node.textContent.trim())) return;
        const item = document.createElement('div'); item.className = 'mcy-info__spec-row';
        const term = document.createElement('dt'); term.textContent = name;
        const value = document.createElement('dd'); nodes.forEach(node => value.append(node));
        item.append(term, value); list.append(item);
      }
      body.replaceChildren(list);
      body.dataset.structured = 'true';
    }
    disconnectedCallback() { this.controller?.abort(); this.resizeObserver?.disconnect(); this.controller = null; }
  });
}
